import { useCallback, useEffect, useState } from "react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Divider } from "primereact/divider";
import { Dropdown } from "primereact/dropdown";
import { FloatLabel } from "primereact/floatlabel";
import { InputText } from "primereact/inputtext";
import ConfirmationWrapper from "../common/ConfirmationWrapper";
import Loading from "../common/Loading";
import NoContent from "../common/NoContent";
import PageTitle from "../common/PageTitle";
import ProgressiveControl from "../common/ProgressiveControl";
import TabHeader from "../common/TabHeader";
import { useAppContext } from "../../providers/ProviderAppContainer";
import { TEXT_BADGE, TEXT_NORMAL, TEXT_SUBTITLE } from "../../style";
import FieldNote from "./common/FieldNote";

const GROUPS = [
    { key: "header", title: "Top menu", note: "Shown in the header and in the phone menu." },
    { key: "footer_quick", title: "Footer quick links", note: "The Quick Links column in the footer. The same page can also be in the top menu. That is a separate link, so you will see it twice." },
    { key: "footer_legal", title: "Footer legal line", note: "Privacy Policy and Terms, on the line under the copyright." },
];

export default function WebsiteLinks() {
    const { requestAPI, showToast } = useAppContext();
    const [loading, setLoading] = useState();
    const [error, setError] = useState();
    const [rows, setRows] = useState();
    const [dialog, setDialog] = useState({ visible: false });

    const loadRows = useCallback(() => {
        requestAPI({
            requestPath: "website/links",
            requestMethod: "GET",
            setLoading,
            onRequestStart: setError,
            onRequestFailure: setError,
            onResponseReceieved: (data, responseCode) => {
                if (data && responseCode === 200) setRows(data);
                else setError("Couldn't load Links");
            },
        });
    }, [requestAPI]);

    useEffect(() => {
        loadRows();
    }, [loadRows]);

    const known = new Set(GROUPS.map((group) => group.key));
    const extras = (rows || []).filter((row) => !known.has(row.group_key));

    return (
        <div className="flex flex-column h-full">
            <PageTitle title="Menu links" />
            <TabHeader
                className="px-3 pt-3"
                title="Menu links"
                highlights={["Home, Courses, and the other pages are listed twice on purpose", "One copy is the top menu. The other copy is the footer. Editing one does not change the other"]}
            />
            <Divider />
            {loading ? <Loading /> : error ? <NoContent error={error} /> : (
                <div className="flex-1 overflow-y-scroll flex flex-column gap-4 p-3">
                    {GROUPS.map((group) => (
                        <section key={group.key} className="flex flex-column gap-2">
                            <div className="flex align-items-center justify-content-between gap-2">
                                <span className={`${TEXT_SUBTITLE} font-semibold`}>{group.title}</span>
                                <Button icon="pi pi-plus" severity="warning" onClick={() => setDialog({ visible: true, record: { group_key: group.key } })} />
                            </div>
                            <p className={`${TEXT_BADGE} text-color-secondary m-0`}>{group.note}</p>
                            {(rows || []).filter((row) => row.group_key === group.key).map((row) => (
                                <Row key={row.id} row={row} setRows={setRows} requestAPI={requestAPI} showToast={showToast} openEdit={(record) => setDialog({ visible: true, record })} />
                            ))}
                        </section>
                    ))}
                    {extras.length ? (
                        <section className="flex flex-column gap-2">
                            <span className={`${TEXT_SUBTITLE} font-semibold`}>Other links</span>
                            <p className={`${TEXT_BADGE} text-color-secondary m-0`}>These are not shown until you move them into the top menu, the footer quick links, or the legal line.</p>
                            {extras.map((row) => (
                                <Row key={row.id} row={row} setRows={setRows} requestAPI={requestAPI} showToast={showToast} openEdit={(record) => setDialog({ visible: true, record })} />
                            ))}
                        </section>
                    ) : null}
                </div>
            )}
            {dialog.visible ? <Editor dialog={dialog} setDialog={setDialog} setRows={setRows} requestAPI={requestAPI} showToast={showToast} /> : null}
        </div>
    );
}

function Row({ row, setRows, requestAPI, showToast, openEdit }) {
    const [deleting, setDeleting] = useState();
    const remove = () => {
        requestAPI({
            requestPath: `website/links/${row.id}`,
            requestMethod: "DELETE",
            setLoading: setDeleting,
            parseResponseBody: false,
            onResponseReceieved: (_, responseCode) => {
                if (responseCode === 204) {
                    showToast({ severity: "success", summary: "Deleted", detail: "Link deleted", life: 1000 });
                    setRows((prev) => prev?.filter((item) => item.id !== row.id));
                } else {
                    showToast({ severity: "error", summary: "Failed", detail: "Failed To Delete !", life: 2000 });
                }
            },
        });
    };
    return (
        <div className="flex gap-3 align-items-center border-round py-2 px-3 text-white bg-green-500">
            <div className="flex flex-column flex-1 overflow-hidden">
                <span className={`${TEXT_NORMAL} font-semibold`}>{row.label || "Untitled"}</span>
                {row.href ? <span className={`${TEXT_BADGE} white-space-nowrap overflow-hidden text-overflow-ellipsis`}>{row.href}</span> : null}
            </div>
            <i className="pi pi-pencil cursor-pointer" onClick={() => openEdit(row)} />
            <ProgressiveControl loading={deleting} control={<ConfirmationWrapper action={remove}><i className="pi pi-trash cursor-pointer" /></ConfirmationWrapper>} />
        </div>
    );
}

function Editor({ dialog, setDialog, setRows, requestAPI, showToast }) {
    const [record, setRecord] = useState({ group_key: "header", ...(dialog.record || {}) });
    const [saving, setSaving] = useState();
    const isNew = !record.id;
    const places = GROUPS.map((group) => ({ label: group.title, value: group.key }));
    const save = () => {
        if (!record.label || !record.href || !record.group_key) {
            showToast({ severity: "error", summary: "Missing details", detail: "Add the link name, the page it opens, and where it appears.", life: 2000 });
            return;
        }
        requestAPI({
            requestPath: "website/links",
            requestMethod: isNew ? "POST" : "PATCH",
            requestPostBody: record,
            setLoading: setSaving,
            onRequestFailure: () => showToast({ severity: "error", summary: "Failed", detail: "Failed To Save !", life: 2000 }),
            onResponseReceieved: (saved, responseCode) => {
                if (saved && (responseCode === 200 || responseCode === 201)) {
                    setRows((prev) => (isNew ? [...(prev || []), saved] : prev.map((item) => (item.id === saved.id ? saved : item))));
                    showToast({ severity: "success", summary: "Saved", detail: "Link saved", life: 1000 });
                    setDialog({ visible: false });
                } else {
                    showToast({ severity: "error", summary: "Failed", detail: saved?.error || "Failed To Save !", life: 2000 });
                }
            },
        });
    };
    return (
        <Dialog header={isNew ? "Add a link" : "Edit link"} visible className="w-11" onHide={() => setDialog({ visible: false })}>
            <div className="flex flex-column">
                <div>
                    <FloatLabel className="mt-5">
                        <Dropdown inputId="group_key" className="w-full" options={places} optionLabel="label" optionValue="value" value={record?.group_key || null} onChange={(event) => setRecord((prev) => ({ ...prev, group_key: event.value }))} disabled={saving} />
                        <label htmlFor="group_key">Where it appears</label>
                    </FloatLabel>
                    <FieldNote text="Top menu, footer quick links, or the legal line. Moving it here does not copy it. The other place keeps its own link." />
                </div>
                <div>
                    <FloatLabel className="mt-5">
                        <InputText id="label" className="w-full" value={record?.label ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, label: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                        <label htmlFor="label">Link name</label>
                    </FloatLabel>
                    <FieldNote text="Required. The words the visitor clicks, such as Courses or Contact Us." />
                </div>
                <div>
                    <FloatLabel className="mt-5">
                        <InputText id="href" className="w-full" value={record?.href ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, href: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                        <label htmlFor="href">Page it opens</label>
                    </FloatLabel>
                    <FieldNote text="Required. A page on this site, such as /courses, or a full address starting with https://." />
                </div>
                <Button className="mt-5 align-self-start" label="Save" icon="pi pi-check" loading={saving} onClick={save} />
            </div>
        </Dialog>
    );
}
